import React, { useState, useContext } from 'react';
import { useDispatch } from "react-redux";
import { CreateDteInnerComment } from 'src/graphql/documents';
import { CreateRepositoryPeriodComment } from 'src/graphql/repository';
import { useContext as useReactContext } from 'react';
import { UserContext } from 'src/context';
import { importAlertState } from 'src/redux/actions/alert';
import { FindDocumentById } from 'src/graphql/documents';
import CommentsTab from 'src/components/comments/CommentsTab';
import { useTranslation } from 'react-i18next';

const CommentsTabDocument = ({ data, contextType = 'document', companyIdOverride, onCommentCreated }) => {
  const { t } = useTranslation('finance');
  const dispatch = useDispatch();
  const userInfo = useReactContext(UserContext);
  const createDteInnerComment = CreateDteInnerComment();
  const createRepositoryPeriodComment = CreateRepositoryPeriodComment();
  const isRepositoryContext = contextType === 'repositoryPeriod';

  const [notes, setNotes] = useState(
    isRepositoryContext ? (data?.innerComments || []) : (data?.meta?.innerComments || []),
  );
  const [isSubmitting, setIsSubmitting] = useState(false);

  const effectiveCompanyId = companyIdOverride || data.companyId;
  const findDocumentById = FindDocumentById({ ids: [data.id] });

  React.useEffect(() => {
    if (isRepositoryContext) {
      setNotes(data?.innerComments || []);
    } else {
      setNotes(data?.meta?.innerComments || []);
    }
  }, [data, isRepositoryContext]);

  const handleSubmitComment = async ({ cleanedValue }) => {
    const baseInput = {
      user: {
        id: userInfo?.id,
        name: userInfo?.name,
        email: userInfo?.email,
      },
      date: new Date(),
      comment: cleanedValue,
    };

    const input = isRepositoryContext
      ? { periodId: data?.id, ...baseInput }
      : { transactionId: data?.id, ...baseInput };

    try {
      setIsSubmitting(true);
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Timeout')), 3000),
      );

      const mutationPromise = isRepositoryContext
        ? onCommentCreated
          ? onCommentCreated(input)
          : createRepositoryPeriodComment.executeMutation({
              periodId: data?.id,
              userId: userInfo?.id,
              userName: userInfo?.name,
              userEmail: userInfo?.email,
              date: baseInput.date,
              comment: baseInput.comment,
            })
        : createDteInnerComment.executeMutation({ input });

      await Promise.race([mutationPromise, timeoutPromise]);

      if (!isRepositoryContext) {
        findDocumentById.reexecuteQuery({ requestPolicy: 'network-only' });
      }
      setNotes((n) => [...n, baseInput]);
    } catch (error) {
      if (error.message === 'Timeout') {
        dispatch(
          importAlertState({
            content: t('comments.timeoutWarning'),
            state: 'warning',
            duration: 5,
          }),
        );
        setNotes((n) => [...n, baseInput]);
      } else {
        dispatch(
          importAlertState({
            content: t('comments.publishError'),
            state: 'error',
            duration: 5,
          }),
        );
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <CommentsTab
      notes={notes}
      companyId={effectiveCompanyId}
      onSubmitComment={handleSubmitComment}
      isSubmitting={isSubmitting}
    />
  );
};

export default CommentsTabDocument;
